import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import LockdownScreen from '../components/LockdownScreen';
import { ShieldAlert, ShieldOff, Save, Eye, EyeOff, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Feb 2026 v7 — Admin → Lockdown.
 *
 * Super-admin only page that flips the platform into maintenance mode.
 * A single confirm re-fires the `PUT /api/admin/lockdown` endpoint.
 * The screen doubles as a live preview so the admin sees exactly what
 * the world will see before pulling the trigger.
 */
export default function AdminLockdownPage() {
  const { token, isSuperAdmin } = useAdminAuth();
  const { toast } = useToast();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const isSuper = !!isSuperAdmin;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    title: '',
    message: '',
    contact: '',
  });

  useEffect(() => {
    if (!isSuper) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/lockdown`, { headers: authHeaders });
        setConfig({
          enabled: !!data.enabled,
          title: data.title || '',
          message: data.message || '',
          contact: data.contact || '',
        });
      } catch (err) {
        toast({ title: 'Failed to load', description: err.response?.data?.detail || 'Try again.' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper]);

  const save = async (nextEnabled) => {
    if (nextEnabled && !window.confirm(
      'Enable Lockdown?\n\nEvery non-super-admin request across the platform will be blocked and shown the maintenance screen. Are you sure?'
    )) return;
    setSaving(true);
    try {
      const body = {
        enabled: !!nextEnabled,
        title: (config.title || '').trim(),
        message: (config.message || '').trim(),
        contact: (config.contact || '').trim(),
      };
      const { data } = await axios.put(`${API}/admin/lockdown`, body, { headers: authHeaders });
      setConfig({
        enabled: !!data.enabled,
        title: data.title || '',
        message: data.message || '',
        contact: data.contact || '',
      });
      toast({
        title: nextEnabled ? 'Lockdown enabled' : 'Lockdown disabled',
        description: nextEnabled
          ? 'Platform is now behind the maintenance screen for every non-super-admin.'
          : 'Platform is live again — all services restored.',
      });
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (!isSuper) {
    return (
      <AdminLayout>
        <div className="p-6 text-sm text-neutral-600">Super admin only.</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6" data-testid="admin-lockdown-page">
        {/* Header */}
        <div className="flex flex-wrap items-start gap-3">
          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${config.enabled ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'}`}>
            {config.enabled ? <ShieldAlert className="w-6 h-6" /> : <ShieldOff className="w-6 h-6" />}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 tracking-tight">System Lockdown</h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              Blackout every non-super-admin request and show the maintenance screen. Use for planned upgrades, data recovery, or emergency triage.
            </p>
          </div>
          {config.enabled && (
            <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-[11px] font-bold uppercase tracking-wider" data-testid="lockdown-status-badge">
              Active
            </span>
          )}
        </div>

        {/* Main toggle */}
        <div className={`rounded-3xl border p-5 ${config.enabled ? 'border-red-200 bg-red-50/50' : 'border-neutral-200 bg-white'}`}>
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-neutral-900">
                Lockdown is currently <span className={config.enabled ? 'text-red-700' : 'text-emerald-700'}>{config.enabled ? 'ON' : 'OFF'}</span>
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">
                {config.enabled
                  ? 'Only you (super admin) can access the platform right now.'
                  : 'All services are live. Toggle ON to enter maintenance mode.'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => save(!config.enabled)}
              disabled={loading || saving}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition disabled:opacity-60 disabled:cursor-not-allowed ${
                config.enabled
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
              data-testid="lockdown-toggle-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (config.enabled ? <ShieldOff className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />)}
              {config.enabled ? 'Disable Lockdown' : 'Enable Lockdown'}
            </button>
          </div>
        </div>

        {/* Message editor */}
        <div className="rounded-3xl border border-neutral-200 bg-white p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="text-sm font-bold text-neutral-900">Maintenance screen copy</div>
            <button
              type="button"
              onClick={() => setShowPreview((p) => !p)}
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-[#0078D4] hover:text-[#005a9e]"
              data-testid="lockdown-preview-btn"
            >
              {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showPreview ? 'Hide preview' : 'Preview screen'}
            </button>
          </div>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Headline</span>
            <input
              type="text" value={config.title}
              onChange={(e) => setConfig({ ...config, title: e.target.value })}
              placeholder="We're getting things back on track"
              className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm"
              data-testid="lockdown-title-input"
              maxLength={80}
            />
            <span className="text-[10px] text-neutral-500 mt-0.5 block">{(config.title || '').length}/80 characters</span>
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Message to customers</span>
            <textarea
              value={config.message}
              onChange={(e) => setConfig({ ...config, message: e.target.value })}
              placeholder="Our team is fine-tuning the platform to serve you better. The store will be back online in a few minutes."
              className="mt-1 w-full min-h-[100px] rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 py-2 text-sm resize-y"
              data-testid="lockdown-message-input"
              maxLength={400}
            />
            <span className="text-[10px] text-neutral-500 mt-0.5 block">{(config.message || '').length}/400 characters</span>
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Contact link (optional)</span>
            <input
              type="text" value={config.contact}
              onChange={(e) => setConfig({ ...config, contact: e.target.value })}
              placeholder="support@mrpco.net or https://…"
              className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm"
              data-testid="lockdown-contact-input"
              maxLength={200}
            />
            <span className="text-[10px] text-neutral-500 mt-0.5 block">
              An email address (rendered as <span className="font-mono">mailto:</span>) or a full URL. Leave blank to hide the "Reach out" button.
            </span>
          </label>

          <div className="pt-2">
            <button
              type="button"
              onClick={() => save(config.enabled)}
              disabled={loading || saving}
              className="inline-flex items-center gap-2 rounded-full bg-[#0078D4] hover:bg-[#005a9e] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-bold px-4 py-2 transition"
              data-testid="lockdown-save-copy-btn"
            >
              <Save className="w-4 h-4" /> Save copy
            </button>
            <span className="ml-3 text-[11px] text-neutral-500">Saved copy is used whenever Lockdown is enabled — you can update it without flipping the toggle.</span>
          </div>
        </div>

        {/* Safety note */}
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2">
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <b>Heads up.</b> While Lockdown is ON, customers can't browse, sub-admins can't sign in, drivers can't route, and the WMS floor apps stop responding — every non-super-admin request 503s with your maintenance screen. Only your super-admin login remains fully operational so you can turn it back OFF.
          </div>
        </div>
      </div>

      {/* Live preview overlay */}
      {showPreview && (
        <div className="fixed inset-0 z-[9998]">
          <LockdownScreen
            title={config.title || 'We\u2019re getting things back on track'}
            message={config.message || 'Our team is fine-tuning the platform to serve you better. The store will be back online in a few minutes.'}
            contact={config.contact || ''}
          />
          <button
            type="button"
            onClick={() => setShowPreview(false)}
            className="fixed top-4 right-4 z-[10000] rounded-full bg-white/90 hover:bg-white text-neutral-900 px-4 py-2 text-sm font-bold shadow-lg"
            data-testid="lockdown-close-preview"
          >
            Close preview
          </button>
        </div>
      )}
    </AdminLayout>
  );
}
